// The Buyer Consulting Fee's two carve rates on a LIVE loan — the answer to
// "what is the number", which was previously findable nowhere on the loan
// record. The fee's EFFECT already had surfaces (the Financials seller
// schedule itemizes each period's carve, and the standing worksheet projects
// the next one); the RATE that produces it had none, so the first sight of the
// fee was after money had already left a seller's distribution.
//
// 🔴 READ-ONLY, AND ITS OWN CARD RATHER THAN A BLOCK INSIDE LoanTermsCard, for
// two reasons that are easy to lose in a refactor:
//
//  1. THE WRITE IS A HIGHER BAR. Reading these is `Loans.Read`; changing them
//     is `PUT /portfolio-admin/loans/{id}/buyer-consulting-fee`, gated on the
//     dedicated `Loans.ManageBuyerConsultingFee` — a permission that is in
//     `Permissions.All` and in NO role map, i.e. ADMIN-only by that absence.
//     LoanTermsCard's editor gates on `Loans.Manage || ADMIN`. Folding these
//     rates into that form would offer a lower bar than the server enforces.
//     Do NOT add them to UpdateLoanTermsRequest.
//
//  2. THEY ARE NOT NOTE TERMS. LoanTermsCard's "Note fee terms" block holds
//     the note's own clauses between borrower and lender — nullable, where a
//     nil legitimately means "the note has no such clause" and renders "—".
//     This is a Phoenix carve out of the SELLER's distribution, stored NOT
//     NULL with a 0.00 off state. Nesting it there would import the wrong nil
//     semantics along with the wrong category.
//
// Rendered UNCONDITIONALLY, zero included. That mirrors this exact fee's own
// precedent on the deal side, whose card renders both percentages on every
// state and swaps only the caption. It deliberately does NOT copy the buyer
// schedule's conditional admin-fee column: that guard is
// `periods.some(p => p.paymentAdministrationFee > 0)`, a per-period MONEY
// predicate, and the equivalent predicate for this fee is false until a
// payment lands — which is precisely the invisibility this card exists to fix.
// A rate is a term, and a term shows its off state.

import { Card } from "@/design-system/components/ui";
import { formatConsultingRatePercent } from "@/lib/buyerConsultingFee";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

/** The one line that keeps this fee from being read as a charge on top of the
 *  buyer's payment. Rendered on EVERY state, including both legs at 0% — the
 *  disambiguation is what the line is for, not a footnote about a value that
 *  happens to be non-zero. (Copied in intent from the deal-side authorization
 *  card, which states the same thing at the moment the terms are captured.) */
const CARVE_NOTE =
  "Carved from the seller's distribution — the buyer's payment amount does not change.";

/** What each leg bills on, in the same vocabulary the engine's own
 *  obligation-type descriptions and the deal-side card use. */
const MONTHLY_BASIS = "Of each scheduled principal & interest payment — never the balloon.";
const LIQUIDITY_BASIS =
  "Of the gross remitted at payoff, balloon, refinance or full prepayment.";

/** Named rather than left to be inferred from an absent line. The standing
 *  worksheet projects the MONTHLY leg onto every future period; the liquidity
 *  leg is event-scoped and sized from the cash actually remitted, so there is
 *  no future period it could attach to and no forecast of it anywhere. A
 *  reader who finds one leg projected and the other missing should be told
 *  why, not left to conclude the second one is free. */
const LIQUIDITY_NOT_PROJECTED =
  "Not forecast on the standing worksheet — it is sized from the cash actually remitted at the event.";

const NOT_CHARGED = "Not charged on this loan.";

interface Props {
  loan: LoanSummaryDto;
}

export function BuyerConsultingFeeTermsCard({ loan }: Props) {
  // `?? 0` because the field is optional on the FE type (so inline fixtures
  // keep compiling) while the wire payload always carries it. 0 is the off
  // state, never "unknown".
  const monthly = loan.buyerConsultingFeeMonthlyPct ?? 0;
  const liquidity = loan.buyerConsultingFeeLiquidityPct ?? 0;

  return (
    <Card data-testid="buyer-consulting-fee-terms-card">
      <div className="mb-space-3">
        <div className="font-display font-bold text-h3 text-onyx">
          Buyer consulting fee
        </div>
        <p className="text-caption text-gray-mid m-0">
          Authorized on the deal at intake and carried onto this loan
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
        <Value label="Monthly fee">
          <span
            className="font-mono tabular-nums"
            data-testid="consulting-monthly-pct"
          >
            {formatConsultingRatePercent(monthly)}
          </span>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            {monthly === 0 ? NOT_CHARGED : MONTHLY_BASIS}
          </p>
        </Value>

        <Value label="Liquidity event fee">
          <span
            className="font-mono tabular-nums"
            data-testid="consulting-liquidity-pct"
          >
            {formatConsultingRatePercent(liquidity)}
          </span>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            {liquidity === 0 ? NOT_CHARGED : LIQUIDITY_BASIS}
          </p>
          {liquidity > 0 && (
            <p className="text-caption text-gray-mid m-0 mt-space-1">
              {LIQUIDITY_NOT_PROJECTED}
            </p>
          )}
        </Value>
      </div>

      <p className="text-caption text-gray-mid m-0 mt-space-3">{CARVE_NOTE}</p>
    </Card>
  );
}

function Value({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-onyx mt-[2px]">{children}</div>
    </div>
  );
}
